<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

const model = defineModel({ type: String, default: '' });
const props = defineProps({
  length: { type: Number, default: 6 },
  invalid: { type: Boolean, default: false },
  /** Seconds before resend is allowed. 0 hides the timer. */
  resendAfter: { type: Number, default: 30 },
});
const emit = defineEmits(['complete', 'resend']);
const boxes = ref([]);
const digits = computed(() => Array.from({ length: props.length }, (_, i) => model.value[i] || ''));
const left = ref(props.resendAfter);
let timer;
const tick = () => { timer = setInterval(() => { if (left.value > 0) left.value -= 1; }, 1000); };
onMounted(tick);
onBeforeUnmount(() => clearInterval(timer));

function set(i, v) {
  const arr = digits.value.slice();
  arr[i] = v;
  model.value = arr.join('').slice(0, props.length);
  if (model.value.length === props.length) emit('complete', model.value);
}
function onInput(i, e) {
  const v = e.target.value.replace(/\D/g, '');
  if (v.length > 1) { model.value = v.slice(0, props.length); boxes.value[Math.min(v.length, props.length) - 1]?.focus(); if (model.value.length === props.length) emit('complete', model.value); return; }
  set(i, v);
  if (v && i < props.length - 1) boxes.value[i + 1].focus();
}
function onKey(i, e) {
  if (e.key === 'Backspace' && !digits.value[i] && i > 0) boxes.value[i - 1].focus();
}
function resend() { left.value = props.resendAfter; emit('resend'); }
</script>

<template>
  <div class="pd-otp">
    <div class="pd-otp__row" role="group" aria-label="One time password">
      <input
        v-for="(d, i) in digits"
        :key="i"
        :ref="(el) => (boxes[i] = el)"
        class="pd-otp__box"
        :class="{ 'is-filled': d, 'is-invalid': invalid }"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        :value="d"
        :aria-label="`Digit ${i + 1}`"
        @input="onInput(i, $event)"
        @keydown="onKey(i, $event)"
      />
    </div>
    <p v-if="resendAfter" class="pd-otp__resend">
      <template v-if="left > 0">Resend code in <strong class="pd-num">0:{{ String(left).padStart(2, '0') }}</strong></template>
      <button v-else type="button" @click="resend">Resend OTP SMS</button>
    </p>
  </div>
</template>

<style scoped>
.pd-otp__row { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; max-width: 420px; }
.pd-otp__box {
  height: 64px;
  min-width: 0;
  text-align: center;
  font-size: 1.75rem;
  font-weight: 800;
  border: 1px solid var(--pd-line);
  border-radius: var(--pd-r-sm);
  background: var(--pd-white);
  outline: none;
  transition: border-color var(--pd-fast) var(--pd-ease), box-shadow var(--pd-fast) var(--pd-ease);
}
.pd-otp__box:focus { border-color: var(--pd-line-active); box-shadow: var(--pd-ring); }
.pd-otp__box.is-filled { border-color: var(--pd-line-active); }
.pd-otp__box.is-invalid { border-color: var(--pd-danger); }
.pd-otp__resend { margin-top: 14px; font-size: 0.875rem; color: var(--pd-muted); }
.pd-otp__resend button { border: 0; background: none; padding: 0; font-weight: 800; color: var(--pd-orange-text); cursor: pointer; }
@media (max-width: 400px) { .pd-otp__box { height: 54px; font-size: 1.4rem; } .pd-otp__row { gap: 6px; } }
</style>
